import { Shield, AlertTriangle, Pause, Play, Trash2 } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { toast } from '@/components/ui/Toaster';
import { useStaff, usePatchStaff, useDeleteStaff } from '@/api/staff';
import { useMe } from '@/api/auth';
import { relativeTime } from '@/lib/format';
import type { StaffRole } from '@/data/types';

const ROLE_LABEL: Record<StaffRole, string> = {
  platform_admin: 'Platform admin',
  sre: 'SRE',
  support: 'Support',
  billing: 'Billing',
  read_only: 'Read-only',
};

const ROLE_TONE: Record<StaffRole, 'neutral' | 'accent' | 'success' | 'warn' | 'danger'> = {
  platform_admin: 'danger',
  sre: 'warn',
  support: 'accent',
  billing: 'success',
  read_only: 'neutral',
};

const ROLE_OPTIONS: StaffRole[] = ['platform_admin', 'sre', 'support', 'billing', 'read_only'];

export function StaffRoute() {
  const { data: apiStaff = [] } = useStaff();
  const { data: me } = useMe();
  const patchStaff = usePatchStaff();
  const deleteStaff = useDeleteStaff();

  async function changeRole(userId: string, role: StaffRole) {
    try {
      await patchStaff.mutateAsync({ userId, role });
      toast.success('Role updated', `Now ${ROLE_LABEL[role]}.`);
    } catch (e: any) {
      toast.danger('Could not change role', e?.message ?? 'Unknown error');
    }
  }

  async function toggleStatus(userId: string, currentStatus: string, email: string) {
    const next = currentStatus === 'active' ? 'suspended' : 'active';
    if (next === 'suspended' && !confirm(`Suspend ${email}? They won't be able to sign in.`)) return;
    try {
      await patchStaff.mutateAsync({ userId, status: next });
      toast.info(next === 'suspended' ? 'Operator suspended' : 'Operator reactivated', email);
    } catch (e: any) {
      toast.danger('Could not change status', e?.message ?? 'Unknown error');
    }
  }

  async function remove(userId: string, email: string) {
    if (!confirm(`Remove ${email}? They can sign in again later if their email is in the allowed domain.`)) return;
    try {
      await deleteStaff.mutateAsync(userId);
      toast.info('Removed', email);
    } catch (e: any) {
      toast.danger('Could not remove operator', e?.message ?? 'Unknown error');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Internal"
        title="Staff & access"
        description="NousViz operators authorised to use this admin. Anyone with an email in the allowed domain auto-creates a 'support' account on first Google login; promote them here."
      />
      <PageBody>
        <Card className="mb-4">
          <CardHeader
            title="Staff members"
            description={`${apiStaff.length} operator${apiStaff.length === 1 ? '' : 's'}`}
          />
          {apiStaff.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-fg-muted">
              No operators yet. The first user to log in via Google SSO becomes a platform admin automatically.
            </div>
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Member</TH>
                  <TH>Role</TH>
                  <TH>2FA</TH>
                  <TH>Last seen</TH>
                  <TH>Status</TH>
                  <TH className="w-32"></TH>
                </tr>
              </THead>
              <TBody>
                {apiStaff.map(s => {
                  const isMe = me?.id === s.id;
                  return (
                    <TR key={s.id}>
                      <TD>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-surface-2 border border-border flex items-center justify-center text-xs font-semibold text-fg">
                            {(s.name || s.email).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('') || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-fg">
                              {s.name || s.email}
                              {isMe && <span className="ml-1.5 text-2xs text-fg-subtle">(you)</span>}
                            </div>
                            <div className="text-2xs text-fg-subtle">{s.email}</div>
                          </div>
                        </div>
                      </TD>
                      <TD>
                        {isMe ? (
                          <Badge tone={ROLE_TONE[s.role]}>{ROLE_LABEL[s.role]}</Badge>
                        ) : (
                          <select
                            value={s.role}
                            onChange={e => changeRole(s.id, e.target.value as StaffRole)}
                            disabled={patchStaff.isPending}
                            className="h-7 px-2 rounded-md bg-surface-2 border border-border hover:border-border-strong focus:border-accent focus:outline-none text-xs"
                          >
                            {ROLE_OPTIONS.map(r => (
                              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                            ))}
                          </select>
                        )}
                      </TD>
                      <TD>
                        {s.twofa ? (
                          <Badge tone="success" icon={<Shield size={10} />}>2FA on</Badge>
                        ) : (
                          <Badge tone="danger" icon={<AlertTriangle size={10} />}>Off</Badge>
                        )}
                      </TD>
                      <TD className="text-fg-muted">{s.last_seen ? relativeTime(s.last_seen) : '—'}</TD>
                      <TD>
                        <Badge tone={s.status === 'active' ? 'success' : 'danger'}>{s.status}</Badge>
                      </TD>
                      <TD>
                        {!isMe && (
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => toggleStatus(s.id, s.status, s.email)}
                              disabled={patchStaff.isPending}
                              className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-subtle hover:text-fg hover:bg-surface-2"
                              title={s.status === 'active' ? 'Suspend' : 'Reactivate'}
                            >
                              {s.status === 'active' ? <Pause size={13} /> : <Play size={13} />}
                            </button>
                            <button
                              onClick={() => remove(s.id, s.email)}
                              disabled={deleteStaff.isPending}
                              className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-subtle hover:text-[color:var(--danger)] hover:bg-surface-2"
                              title="Remove"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader title="Access policy" description="How operators get access to this admin." />
          <CardBody className="text-sm text-fg-muted space-y-2 leading-relaxed">
            <p>
              Sign-in is via <strong className="text-fg">Google Workspace SSO</strong> restricted to{' '}
              <code className="font-mono text-xs">@nousviz.com</code>. Anyone with a verified email on
              that domain auto-creates a <code className="font-mono text-xs">support</code> account on
              first login.
            </p>
            <p>
              Promote new operators here. The first user to log in becomes <code className="font-mono text-xs">platform_admin</code>{' '}
              automatically. You can't change your own role or status (avoids accidental lock-out — ask another admin).
            </p>
          </CardBody>
        </Card>
      </PageBody>
    </>
  );
}
